import { AppSidebar, type SidebarGroup, type SidebarItem } from '@/components/layout/AppSidebar';
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { UserChip } from '@/components/layout/UserChip';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { useIsMobile } from '@/lib/useIsMobile';
import { useSidebarCollapsed } from '@/lib/useSidebarCollapsed';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  ArrowLeft,
  BookOpen,
  FileUp,
  FolderOpen,
  History,
  MessageSquare,
  Network,
  Search,
  Upload,
} from 'lucide-react';
import { useEffect, useRef } from 'react';

export function WikiLayout() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const { collapsed, toggle } = useSidebarCollapsed();
  const isMobile = useIsMobile();
  // 모바일 첫 진입 시 사이드바 자동 collapse.
  const mobileInitialCollapsedRef = useRef(false);
  useEffect(() => {
    if (isMobile && !mobileInitialCollapsedRef.current && !collapsed) {
      mobileInitialCollapsedRef.current = true;
      toggle();
    }
    if (!isMobile) mobileInitialCollapsedRef.current = false;
  }, [isMobile, collapsed, toggle]);

  // 모바일 + 사이드바 열림 + 라우트 변경 → 자동 close.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      if (isMobile && !collapsed) toggle();
    }
  }, [location.pathname, isMobile, collapsed, toggle]);

  // 인증 가드는 router 의 wikiLayoutRoute.beforeLoad 에서 mount 이전에 처리.
  // 여기 useEffect 가드를 두면 첫 paint 한 tick 동안 stale null 캐시로 잘못 튕겨서
  // /login → /chat 메인으로 두세 번 바운스하는 race 가 생겼었음.

  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories'],
    queryFn: api.wikiCategories,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  if (isLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground-muted text-sm">
        로딩 중…
      </div>
    );
  }

  const categories = categoriesQuery.data?.categories ?? [];

  const categoryItems: SidebarItem[] = categories.map((c) => ({
    label: `${c.name}${c.pageCount !== undefined ? ` (${c.pageCount})` : ''}`,
    to: `/wikis/c/${c.slug}`,
    icon: FolderOpen,
  }));

  const groups: SidebarGroup[] = [
    {
      items: [
        { label: '위키 목차', to: '/wikis', icon: BookOpen },
        { label: '지식 그래프', to: '/wikis/graph', icon: Network },
        { label: '작업 이력', to: '/wikis/log', icon: History },
        { label: '검색', to: '/wikis/search', icon: Search },
        { label: '내 자료', to: '/wikis/documents', icon: FileUp },
        { label: '자료 올리기', to: '/wikis/upload', icon: Upload },
        { label: '채팅', to: '/chat', icon: MessageSquare },
      ],
    },
    ...(categoryItems.length > 0 ? [{ heading: '카테고리', items: categoryItems }] : []),
  ];

  const sidebarOpen = !collapsed;
  // /wikis/graph (Aurora Atlas) 는 fullscreen — 사이드바와 opener 모두 숨김.
  const isGraphRoute = location.pathname === '/wikis/graph';
  // 채팅에서 진입한 경우 (?from=chat&chatId=…) — 위키 사이드바 숨기고 "채팅으로 돌아가기" 헤더만.
  const chatSearchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const fromChat = chatSearchParams?.get('from') === 'chat';
  const fromChatId = chatSearchParams?.get('chatId') ?? null;
  const chatReturnHref = fromChatId ? `/chat/${encodeURIComponent(fromChatId)}` : '/chat';
  const showOpenerBar = !isGraphRoute && !fromChat && (collapsed || isMobile);
  return (
    <div className="h-[100dvh] flex bg-background text-foreground overflow-hidden">
      {sidebarOpen && !isMobile && !isGraphRoute && !fromChat ? (
        <AppSidebar
          brand="위키"
          groups={groups}
          activePath={location.pathname}
          width={256}
          footer={<UserChip allowThemeToggle />}
          onClose={toggle}
        />
      ) : null}
      <main className={cn('flex-1 flex flex-col min-w-0 relative overflow-y-auto')}>
        {fromChat ? (
          <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
            <Link
              to={chatReturnHref as string}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              채팅으로 돌아가기
            </Link>
          </div>
        ) : showOpenerBar ? (
          <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
            <SidebarOpenerButton />
          </div>
        ) : null}
        <Outlet />
      </main>
      {sidebarOpen && isMobile && !isGraphRoute && !fromChat ? (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop 은 사이드바 헤더 X 로도 닫을 수 있음 */}
          <div
            role="button"
            tabIndex={-1}
            aria-label="사이드바 닫기"
            onClick={toggle}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px]"
          />
          <div className="fixed inset-y-0 left-0 z-50 flex shadow-2xl">
            <AppSidebar
              brand="위키"
              groups={groups}
              activePath={location.pathname}
              width={256}
              footer={<UserChip allowThemeToggle />}
              onClose={toggle}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
